import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { translations, PRACTICE_AREAS_ES, INSIGHTS_ES } from "@/i18n/translations";

const STORAGE_KEY = "rinnou_lang";

const LanguageContext = createContext(null);

const getNested = (dict, path) => {
  const parts = path.split(".");
  let node = dict;
  for (const p of parts) {
    if (node == null) return undefined;
    node = node[p];
  }
  return node;
};

// Replaces {{token}} placeholders in a string with values from `vars`.
// Used for legal-page copy that mixes translated sentences with firm data
// (name, founder, phone) that shouldn't live inside the translation dictionary.
export const fillTemplate = (str, vars = {}) =>
  Object.entries(vars).reduce((acc, [key, value]) => acc.split(`{{${key}}}`).join(value), str);

/**
 * Lightweight frontend-only i18n provider (English / Spanish).
 * Scope: Home, Header/Footer, Practice Area overview (title + short intro +
 * services), Contact form, and CTAs. Persists the chosen language in
 * localStorage so it survives navigation and page reloads.
 */
export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved === "en" || saved === "es") return saved;
    } catch (err) {
      // localStorage unavailable (e.g. private mode) — fall back to English
      console.warn("LanguageProvider: couldn't read saved language preference", err);
    }
    return "en";
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch (err) {
      // localStorage unavailable/full — language still works for this
      // session, it just won't persist across reloads.
      console.warn("LanguageProvider: couldn't persist language preference", err);
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const toggleLanguage = useCallback(() => {
    setLang((prev) => (prev === "en" ? "es" : "en"));
  }, []);

  const t = useCallback(
    (path, fallback) => {
      const dict = translations[lang] || translations.en;
      let value = getNested(dict, path);
      if (value === undefined) {
        value = getNested(translations.en, path);
      }
      if (value === undefined) {
        return fallback !== undefined ? fallback : path;
      }
      return value;
    },
    [lang]
  );

  // Returns the Spanish content override for a practice area slug (title,
  // heroTagline, overview, services, bullets) — null when English is active
  // or no override exists, so callers can safely fall back to backend data.
  const getPracticeAreaOverride = useCallback(
    (slug) => (lang === "es" ? PRACTICE_AREAS_ES[slug] || null : null),
    [lang]
  );

  // Same pattern for Insights articles (title/excerpt/metaDescription/sections).
  const getInsightOverride = useCallback(
    (slug) => (lang === "es" ? INSIGHTS_ES[slug] || null : null),
    [lang]
  );

  const value = useMemo(
    () => ({
      lang,
      isSpanish: lang === "es",
      setLang,
      toggleLanguage,
      t,
      getPracticeAreaOverride,
      getInsightOverride,
    }),
    [lang, toggleLanguage, t, getPracticeAreaOverride, getInsightOverride]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return ctx;
};
